// Reusable Active/Inactive status badge + click-to-toggle for master list rows.
//
// Default field is `is_active` (used by Client/Item/Vendor). For Member (which
// uses legacy field `active`), pass `field="active"`.
//
// Usage:
//   <ActiveStatusCell row={row} resource="items" onChanged={reload} />
//   <ActiveStatusCell row={row} resource="members" field="active" onChanged={reload} />

import { useState } from "react";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

export function ActiveStatusCell({ row, resource, field = "is_active", onChanged }) {
  const [busy, setBusy] = useState(false);
  // Treat missing/undefined value as ACTIVE (legacy records pre-feature default to active).
  const isActive = row?.[field] !== false;

  async function toggle(e) {
    e.stopPropagation();
    if (busy) return;
    const next = !isActive;
    const confirmMsg = next
      ? `Mark "${row.name || row.client_code || row.item_code || row.vendor_code || row.member_code || "this record"}" as ACTIVE?`
      : `Mark "${row.name || row.client_code || row.item_code || row.vendor_code || row.member_code || "this record"}" as INACTIVE? It will be hidden from transaction dropdowns by default.`;
    if (!window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      await api.put(`/${resource}/${row.id}`, { ...row, [field]: next });
      toast.success(next ? "Marked Active" : "Marked Inactive");
      if (onChanged) onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update status");
    } finally {
      setBusy(false);
    }
  }

  const Icon = isActive ? CheckCircle2 : XCircle;
  const cls = isActive
    ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
    : "bg-slate-100 text-slate-500 border-slate-300 hover:bg-slate-200";
  return (
    <button
      onClick={toggle}
      disabled={busy}
      data-testid={`toggle-active-${row.id}`}
      title={`Click to mark ${isActive ? "Inactive" : "Active"}`}
      className={`inline-flex items-center gap-1.5 px-2 py-1 text-[10px] uppercase tracking-wider font-bold border ${cls} disabled:opacity-60`}
    >
      <Icon size={12} /> {isActive ? "Active" : "Inactive"}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Active/Inactive list-filter dropdown — pairs with ActiveStatusCell. */
/* ------------------------------------------------------------------ */
export function ActiveStatusFilter({ value, onChange, testid }) {
  return (
    <select
      data-testid={testid || "active-status-filter"}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#F97316]"
    >
      <option value="active">Active Only</option>
      <option value="inactive">Inactive Only</option>
      <option value="all">All (Active + Inactive)</option>
    </select>
  );
}

/* Filter helper used by list pages — default `field` is `is_active`. */
export function matchActiveFilter(row, mode, field = "is_active") {
  const isActive = row?.[field] !== false;
  if (mode === "active") return isActive;
  if (mode === "inactive") return !isActive;
  return true;
}
